// no-dd-sa:typescript-best-practices/boolean-prop-naming
import { useEffect, useRef, useState, RefObject } from 'react';

interface UseInViewOptions {
  /** Intersection threshold (0-1). Default: 0.1 */
  threshold?: number;
  /** Root margin for early/late triggering. Default: '0px' */
  rootMargin?: string;
  /** Only trigger once (default: true) */
  triggerOnce?: boolean;
}

/**
 * Hook that returns whether an element is in the viewport.
 * Respects prefers-reduced-motion by immediately returning true.
 *
 * @example
 * const [ref, isInView] = useInView<HTMLDivElement>();
 * return <div ref={ref} className={isInView ? 'opacity-100' : 'opacity-0'}>...</div>
 */
export function useInView<T extends HTMLElement = HTMLDivElement>(
  options: UseInViewOptions = {}
): [RefObject<T>, boolean] {
  const { threshold = 0.1, rootMargin = '0px', triggerOnce = true } = options;
  const ref = useRef<T>(null) as RefObject<T>;
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    // Respect prefers-reduced-motion - immediately show content
    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;

    if (prefersReducedMotion) {
      setIsInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          if (triggerOnce) {
            observer.unobserve(element);
          }
        } else if (!triggerOnce) {
          setIsInView(false);
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [threshold, rootMargin, triggerOnce]);

  return [ref, isInView];
}